import React, { Fragment, useState } from 'react'
import { scroller } from 'react-scroll'
import {
  Button,
  Badge,
  Container,
  ExpandableButton,
  TextTitle,
  TextBody,
  VariantList,
  ListContainer,
  Css,
  Icon,
  Tools,
  HtmlContent
} from '@pmi/pmi-iqos-ds-react'
import { htmlTranslation, Price } from '@pmi/pmi-iqos-shared-react'
import { AddToCartButton, ShortestDeliveryDate } from '@dcx-common/components'

const actionBlockId = 'product-detail-actions'
const descriptionBlockId = 'bundle-detail-description'

const ProductBundleDetailContent = ({
  selectedProducts,
  highlightedProduct,
  loading,
  loadingButton,
  extraDataLoaded,
  products,
  name,
  badge,
  currency,
  shortDescription,
  fullDescription,
  translations,
  shortestDeliveryDate,
  backgroundColor,
  imageBlockId,
  onColorSelected,
  onAddToCart,
  onReset,
  promotionBanner,
  descriptionLine,
  line
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)

  const handleColorChange = productIndex => optionIndex => {
    onColorSelected(productIndex, optionIndex)
  }

  const handleAddToCart = () => {
    onAddToCart()
  }

  const handleResetCart = () => {
    onReset()
  }

  const handleReadMoreClick = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded)
    if (isDescriptionExpanded) {
      scroller.scrollTo(actionBlockId, {
        duration: 0,
        smooth: true
      })
    }
  }

  const isReadMoreNecessary = () => {
    if (
      (loading && !loadingButton) ||
      typeof document === 'undefined' ||
      typeof window === 'undefined'
    ) {
      return false
    }
    const imageBlock = document.getElementById(imageBlockId)
    const actionBlock = document.getElementById(actionBlockId)
    const descriptionBlock = document.getElementById(descriptionBlockId)
    if (Tools.isDesktop()) {
      // Desktop
      return (
        imageBlock &&
        actionBlock &&
        descriptionBlock &&
        actionBlock.scrollHeight + descriptionBlock.scrollHeight >
          imageBlock.scrollHeight + 70 // Little additional margin to prevent read more for 1 line
      )
    } else {
      return (
        descriptionBlock &&
        descriptionBlock.scrollHeight > (window.innerHeight * 50) / 100 + 70 // Little additional margin to prevent read more for 1 line
      ) // 50vh
    }
  }

  const getDescriptionHeight = () => {
    const imageBlock =
      typeof document !== 'undefined' && document.getElementById(imageBlockId)
    const actionBlock =
      typeof document !== 'undefined' && document.getElementById(actionBlockId)
    const diff =
      (imageBlock && imageBlock.scrollHeight) -
      (actionBlock && actionBlock.scrollHeight)
    return isDescriptionExpanded || !isReadMoreNecessary()
      ? 'auto'
      : ['50vh', '50vh', diff ? `calc(${diff}px - 2rem)` : '0']
  }

  const selectedOption =
    products[highlightedProduct].options[selectedProducts[highlightedProduct]]
  const variantDescription =
    line && selectedOption.descriptionLine
      ? selectedOption.descriptionLine
      : selectedOption.description
  const bundlePrice = Object.keys(selectedProducts)
    .map(key => {
      return products[key].options[selectedProducts[key]].price
    })
    .reduce((a, b) => a + b, 0)
  const bundleListPrice = Object.keys(selectedProducts)
    .map(key => {
      return products[key].options[selectedProducts[key]].listPrice
    })
    .reduce((a, b) => a + b, 0)
  const inStock = (Object.keys(selectedProducts) || []).every(
    key => products[key].options[selectedProducts[key]].inStock
  )

  return (
    <Container px={['0', '0', '24']}>
      <Css
        s={{
          overflow: 'hidden',
          position: 'relative',
          '::after': isReadMoreNecessary() && {
            content: '""',
            position: 'absolute',
            height: '1rem',
            bottom: 0,
            left: '-1rem',
            right: '-1rem',
            boxShadow: isDescriptionExpanded
              ? 'none'
              : `inset ${backgroundColor} 0 -1rem 1rem`
          }
        }}>
        <Container id={actionBlockId}>
          {(products[highlightedProduct].family || badge) && (
            <ListContainer xBetween="2" yBetween="2">
              {badge && (
                <Badge
                  text={badge.title}
                  color={badge.font_colour}
                  background={badge.background_colour}
                  data-testing="BundleProductDetail_Badge_BundleHint"
                />
              )}
              {products[highlightedProduct].family && (
                <Badge
                  text={products[highlightedProduct].family.name}
                  color={products[highlightedProduct].family.colour}
                  border
                  data-testing="BundleProductDetail_Badge_BundleFamily"
                />
              )}
            </ListContainer>
          )}
          {(name || translations.tk_bundleName) && (
            <Container display={Container.displays.flex} pb="4">
              <TextTitle
                heading={TextTitle.headings.h3}
                data-testing="BundleProductDetail_TextTitle_BundleName">
                {name || translations.tk_bundleName}
              </TextTitle>
            </Container>
          )}
          <Container pb="8">
            {products.map((product, i) => {
              return (
                i === highlightedProduct && (
                  <Fragment key={i}>
                    {product.options[selectedProducts[i]].shortVariantName &&
                      product.options[selectedProducts[i]].shortVariantName
                        .length > 0 && (
                        <Container pb="4">
                          <TextBody
                            size={TextBody.sizes.m}
                            data-testing="BundleProductDetail_TextBody_ShortVariantName">
                            {
                              product.options[selectedProducts[i]]
                                .shortVariantName
                            }
                          </TextBody>
                        </Container>
                      )}
                    <Container>
                      <VariantList
                        variants={product.options}
                        selectedVariantIndex={selectedProducts[i]}
                        handleVariantIndexChange={handleColorChange(i)}
                        data-testing-prefix="ProductBundleDetail"
                      />
                    </Container>
                  </Fragment>
                )
              )
            })}
          </Container>
          {shortDescription && (
            <Container pb="6">
              <TextBody
                size={TextBody.sizes.m}
                data-testing="BundleProductDetail_TextBody_Description"
                acceptsHtml
                value={shortDescription}
              />
            </Container>
          )}
          <Container pb="4">
            <Price
              size={Price.sizes.large}
              price={bundlePrice}
              listPrice={bundleListPrice}
              currency={currency}
              listPriceLabel={translations.tk_listPriceDescription}
              discountedPriceLabel={translations.tk_discountedPriceDescription}
              taxLabel={translations.tk_withTax}
              data-testing="ProductBundleDetail_Price"
            />
          </Container>
          {!inStock && extraDataLoaded && (
            <Container pb="6">
              <TextBody
                style={TextBody.styles.error}
                {...htmlTranslation(translations, 'tk_outOfStock')}
              />
            </Container>
          )}
          <Container pb="8">
            <ListContainer xBetween="6" yBetween="4">
              <Button
                data-testing="ProductBundleDetail_Button_Reset"
                text={translations.tk_actionReset}
                onClickHandler={handleResetCart}
                variant={Button.variants.secondary}
              />
              <AddToCartButton
                maxQty={inStock}
                hasStock={inStock}
                addToCartHandler={handleAddToCart}
                loading={loadingButton}
                addToCartLabel={translations.tk_actionAddToCart}
                data-testing="ProductBundleDetail_Button_AddToCart"
              />
            </ListContainer>
          </Container>
        </Container>
        {promotionBanner && (
          <Container pb="6">
            <HtmlContent content={promotionBanner} />
          </Container>
        )}
        {shortestDeliveryDate && (
          <Container pb="4">
            <ShortestDeliveryDate
              shortestDeliveryDate={shortestDeliveryDate}
              shortestDateLabel={translations.tk_shortestDateLabel}
              shortestDateNote={translations.tk_shortestDateNote}
            />
          </Container>
        )}
        <Container pb="8">
          <Css
            id={descriptionBlockId}
            s={{
              height: getDescriptionHeight(),
              minHeight: '6rem'
            }}>
            <Container data-testing="BundleProductDetail_TextBody_FullDescription">
              <TextBody
                acceptsHtml
                value={
                  line && descriptionLine ? descriptionLine : fullDescription
                }
              />
              {variantDescription && (
                <TextBody acceptsHtml value={variantDescription} />
              )}
            </Container>
          </Css>
        </Container>
      </Css>
      {isReadMoreNecessary() && (
        <ExpandableButton
          onClickHandler={handleReadMoreClick}
          icon={
            isDescriptionExpanded
              ? Icon.images.chevronUp
              : Icon.images.chevronDown
          }
          iconPosition={ExpandableButton.iconPositions.right}
          textTemplate={
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(
                translations,
                isDescriptionExpanded ? 'tk_readLess' : 'tk_readMore'
              )}
            />
          }
          data-testing="ProductBundleDetail_Button_ReadMore"
        />
      )}
    </Container>
  )
}

export default ProductBundleDetailContent
